import { StepIndex, VerticalStep } from '@code/innovance-react-ui-kit';
import React, { FC, Fragment, ReactElement, useCallback, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  ProductOperationsCardApply,
  ProductOperationsCardApplySummary,
  ProductOperationsClientInfoChange,
  ProductOperationsContactInfoChange,
  ProductOperationsReissue,
} from 'src/front/api/apiInstance';
import { OperationsReissueGetResponse } from 'src/front/api/generated';
import Stepper, { getProgressConfig } from 'src/front/components/Stepper';

import StepperView, { StepperRefProps } from 'src/front/components/StepperView';

import CardEntryProvider from 'src/front/contexts/CardEntryContext';
import CardReissueProvider from 'src/front/contexts/CardReissueContext';
import ClientInfoProvider from 'src/front/contexts/ClientInfoContext';
import ContactInfoProvider from 'src/front/contexts/ContactInfoContext';
import ExecutedOperationProvider, {
  useClearDispatch,
} from 'src/front/contexts/ExecutedOperationContext';
import ApplicationDocumentModal from 'src/front/pages/applications/card-entry/ApplicationDocumentUpload';
import CardApplicationDetail from 'src/front/pages/applications/card-entry/card-information/CardApplicationDetail';
import CardSendingInformation from 'src/front/pages/applications/card-entry/card-information/CardSendingInformation';
import ProductOffers from 'src/front/pages/applications/card-entry/card-information/ProductOffers';
import ProductInformation from 'src/front/pages/applications/card-entry/product-information/ProductInformation';
import CardSummary from 'src/front/pages/applications/card-entry/Summary';
import CardReissueIssue from 'src/front/pages/applications/card-reissue/CardReissueIssue';
import ClientInfoDocumentUpload from 'src/front/pages/applications/client-info/ClientInfoDocumentUpload';
import DocumentTaxInformation from 'src/front/pages/applications/client-info/DocumentTaxInformation';
import IndividualInformation from 'src/front/pages/applications/client-info/IndividualInformation';
import ClientInformation from 'src/front/pages/applications/contact-info/ClientInformation';
import ContactInfoDocumentUpload from 'src/front/pages/applications/contact-info/ContactInfoDocumentUpload';
import ContactInformation from 'src/front/pages/applications/contact-info/ContactInformation';

import {
  CARD_ENTRY,
  CARD_REISSUE,
  CLIENT_INFO,
  CONTACT_INFO,
  RESULT,
  SubMenuRoutes,
} from 'src/front/routes/paths';

import OperationSearch from './OperationSearch';

interface StepTextIndex {
  text: string;
  component: () => ReactElement;
}

interface OptionalStepDefinition {
  [path: string]: StepTextIndex[];
}

interface ProviderType {
  [path: string]: FC;
}

export interface BaseOperationInfo {
  referenceNumber: string;
}

export interface OperationInfo extends BaseOperationInfo {
  menuId: string;
  customerName?: string;
}

export interface OperationDataInfo extends BaseOperationInfo {
  operationData?: any;
  clientName?: string;
}

const PROVIDERS: ProviderType = {
  [CARD_ENTRY]: CardEntryProvider,
  [CARD_REISSUE]: CardReissueProvider,
  [CLIENT_INFO]: ClientInfoProvider,
  [CONTACT_INFO]: ContactInfoProvider,
};

const ExecutedOperations: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const clearOldData = useClearDispatch();

  const stepperRef = useRef<StepperRefProps>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<StepIndex>({ main: 1, sub: 1 });
  const [customerId, setCustomerId] = useState<string>();
  const [selectedPath, setSelectedPath] = useState<string>();
  const [operationInfo, setOperationInfo] = useState<OperationDataInfo>();
  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  const onEditCardEntry = useCallback(async (info: OperationInfo): Promise<void> => {
    const path = SubMenuRoutes[info.menuId];
    let remoteData: any;

    if (path === CARD_ENTRY) {
      const { data } = await ProductOperationsCardApply.getCardApplicationDataByReferenceNumber({
        referenceNumber: info.referenceNumber,
      });
      remoteData = data;
    } else if (path === CARD_REISSUE) {
      const { data } = await ProductOperationsReissue.getReissueDataByReferenceNumber({
        referenceNumber: info.referenceNumber,
      });

      remoteData = data;
    } else if (path === CLIENT_INFO) {
      const { data } = await ProductOperationsClientInfoChange.getClientInfoDataByReferenceNumber({
        referenceNumber: info.referenceNumber,
      });
      remoteData = data;
    } else if (path === CONTACT_INFO) {
      const { data } = await ProductOperationsContactInfoChange.getContactInfoChangeData({
        referenceNumber: info.referenceNumber,
      });
      remoteData = data;
    }
    if (remoteData?.result === 'OK') {
      setSelectedPath(path);
      setOperationInfo({
        operationData: remoteData,
        referenceNumber: info.referenceNumber,
        clientName: info.customerName,
      });
      stepperRef.current?.goToNextStep();
    } else {
      throw new Error('Fetching is failed');
    }
  }, []);
  const goToNextStep = useCallback(
    async (id?: string): Promise<void> => {
      setCustomerId(id);
      if (selectedPath === CARD_ENTRY && currentIndex === 3) {
        try {
          const { data } = await ProductOperationsCardApplySummary.prepareSummaryData({});
          if (data.result === 'OK') {
            stepperRef.current?.goToNextStep();
          }
        } catch {
          // todo(ctasci): it's handled with error boundary
        }
      } else {
        stepperRef.current?.goToNextStep();
      }
    },
    [currentIndex, selectedPath]
  );

  const goToSuccessStep = useCallback(
    (message: string): void => history.push(RESULT, { message }),
    [history]
  );

  const goToFirstStep = useCallback((): void => {
    clearOldData();
    setSelectedPath(undefined);
    setIsCompleted(false);
    stepperRef.current?.goToFirstStep();
    // due to clearOldData's reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const optionalSteps = useMemo(
    (): OptionalStepDefinition => ({
      [CARD_ENTRY]: [
        {
          text: t('cardEntry_stepper_productStep_title'),
          component: () => (
            <ProductInformation
              goToNextStep={goToNextStep}
              isProductOfferFlow={operationInfo?.operationData.isProductOffer}
              isEdit
            />
          ),
        },
        ...(operationInfo?.operationData.isProductOffer
          ? [
              {
                text: t('cardEntry_stepper_productOffersStep_title'),
                component: () => (
                  <ProductOffers
                    goToFailureStep={goToSuccessStep}
                    goToNextStep={goToNextStep}
                    isEdit
                  />
                ),
              },
            ]
          : []),
        {
          text: t('cardEntry_stepper_cardStepSub1_title'),
          component: () => (
            <CardApplicationDetail
              customerId={operationInfo?.operationData.customerId}
              goToNextStep={goToNextStep}
              isEdit
            />
          ),
        },
        {
          text: t('cardEntry_stepper_cardStepSub2_title'),
          component: () => (
            <CardSendingInformation
              customerId={operationInfo?.operationData.customerId}
              goToNextStep={goToNextStep}
              isProductOfferEdit={!!operationInfo?.operationData.isProductOffer}
            />
          ),
        },
        {
          text: t('cardEntry_stepper_summaryStep_title'),
          component: () => (
            <CardSummary
              customerId={operationInfo?.operationData.customerId}
              goToNextStep={goToNextStep}
            />
          ),
        },
        {
          text: t('cardEntry_stepper_documentUploadStep_title'),
          component: () => (
            <ApplicationDocumentModal
              customerId={operationInfo?.operationData.customerId}
              goToFirstStep={goToFirstStep}
              goToSuccessStep={goToSuccessStep}
              isEdit
            />
          ),
        },
      ],
      [CARD_REISSUE]: [
        {
          text: t('cardReissue_stepper_cardReissue_text'),
          component: () => (
            <CardReissueIssue
              goToFirstStep={goToFirstStep}
              operationsReissueData={operationInfo?.operationData as OperationsReissueGetResponse}
              referenceNumber={operationInfo?.referenceNumber}
              showIncomeLabels={false}
              isEdit
            />
          ),
        },
      ],
      [CLIENT_INFO]: [
        {
          text: t('changeFundClientInfo_stepper_changeClientInfoStepSub1_text'),
          component: () => (
            <IndividualInformation
              goToNextStep={goToNextStep}
              isEditCustomerName={operationInfo?.clientName}
              isEdit
            />
          ),
        },
        {
          text: t('changeFundClientInfo_stepper_changeClientInfoStepSub2_text'),
          component: () => (
            <DocumentTaxInformation
              goToNextStep={goToNextStep}
              isEditCustomerName={operationInfo?.clientName}
              isEdit
            />
          ),
        },
        {
          text: t('changeFundClientInfo_stepper_changeClientInfoStepSub3_text'),
          component: () => (
            <ClientInfoDocumentUpload
              isEditCustomerName={operationInfo?.clientName}
              onRepeat={goToFirstStep}
              setIsCompleted={setIsCompleted}
              isEdit
            />
          ),
        },
      ],
      [CONTACT_INFO]: [
        {
          text: t('cardEntry_stepper_customerStep_title'),
          component: () => <ClientInformation goToNextStep={goToNextStep} isEdit />,
        },
        {
          text: t('changeContactInfo_stepper_changeClientContactInfoStepSub1_text'),
          component: () => (
            <ContactInformation
              contactCustomerName={operationInfo?.clientName}
              customerId={customerId}
              goToNextStep={goToNextStep}
              isEdit
            />
          ),
        },
        {
          text: t('changeContactInfo_stepper_changeClientContactInfoStepSub2_text'),
          component: () => (
            <ContactInfoDocumentUpload
              contactCustomerName={operationInfo?.clientName}
              onRepeat={goToFirstStep}
              setIsCompleted={setIsCompleted}
              isEdit
            />
          ),
        },
      ],
    }),
    [customerId, goToFirstStep, goToNextStep, goToSuccessStep, operationInfo, t]
  );

  const steps = useMemo(
    () => [
      {
        component: () => <OperationSearch onEditCardEntry={onEditCardEntry} />,
      },
      ...(selectedPath
        ? optionalSteps[selectedPath].map((step) => ({
            component: step.component,
          }))
        : []),
    ],
    [onEditCardEntry, optionalSteps, selectedPath]
  );

  const stepsVertical = useMemo(() => {
    return [
      {
        index: 1,
        text: t('executedOperations_stepper_search_text'),
      },
      {
        index: 2,
        text:
          selectedPath === CLIENT_INFO
            ? t('changeFundClientInfo_stepper_changeClientInfo_text')
            : t('executedOperations_stepper_editOperation_text'),
        ...(selectedPath
          ? {
              subSteps: optionalSteps[selectedPath].map((step, index) => ({
                index: index + 1,
                text: step.text,
              })),
            }
          : {}),
      },
    ];
  }, [optionalSteps, selectedPath, t]);

  const stepMapping: StepIndex[] = useMemo(() => {
    const emptyList: StepIndex[] = [];
    return stepsVertical.reduce(
      (prevSteps, current) => [
        ...prevSteps,
        ...(current.subSteps
          ? current.subSteps.map((item) => ({ main: current.index, sub: item.index }))
          : [{ main: current.index, sub: 1 }]),
      ],
      emptyList
    );
  }, [stepsVertical]);

  const onStepChange = (index: number): void => {
    setCurrentStep(stepMapping[index]);
    setCurrentIndex(index);
  };

  const handleChangeStep = useCallback(
    (main: VerticalStep, sub?: VerticalStep): void => {
      const index: number = stepMapping.findIndex(
        (step) => step.main === main.index && step.sub === (sub?.index || 1)
      );
      if (index !== -1) {
        setCurrentIndex(index);
        stepperRef.current?.goToStep(index);

        if (index === 0) {
          setSelectedPath(undefined);
        }
      }
    },
    [stepMapping]
  );

  const OperationProvider = useMemo(() => {
    return selectedPath ? PROVIDERS[selectedPath] : Fragment;
  }, [selectedPath]);

  return (
    <OperationProvider>
      <StepperView onChange={onStepChange} ref={stepperRef} steps={steps} />
      <Stepper
        currentStep={currentStep}
        handleChangeStep={handleChangeStep}
        progressConfig={getProgressConfig(currentIndex, steps.length, 100, t, isCompleted)}
        stepConfig={stepsVertical}
      />
    </OperationProvider>
  );
};

export default (): ReactElement => (
  <ExecutedOperationProvider>
    <ExecutedOperations />
  </ExecutedOperationProvider>
);
